import React, { useState, useEffect, useCallback } from "react";
import { Form, Input, Button } from "antd";
import { Header, useCommonInfo, redirectIfNotCurrent, DevUpdateUrls, CurrentSalaryInfo } from "./common";
import { dotReg, formatCurrency } from "../../legacy_components/util";
import { RemainingAttemptsText } from "./Trial";
import { GB_FLAGS, withLDFlagsFromBackend } from "../../../dv2_temporary_solution/services/launchDarkly";

const suffixPrefixStyle = { paddingTop: "35px" };

const RateUpdateForm = ({ form, LDFlags }) => {
  const {
    getFieldDecorator,
    getFieldsError,
    setFieldsValue,
    getFieldsValue,
  } = form;
  const [isDisabled, setDisabled] = useState(true);
  const {
    jobPosition,
    yearsOfExperience,
    expectedRateCurrency,
    firstName = "",
    saveProfileData,
    remainingAttempts,
    submittedRate = null
  } = useCommonInfo();

  const {
    dev_rate_reduction_show_current_rate = false
  } = LDFlags || {};

  useEffect(() => {
    setFieldsValue({
      yearsOfExperience: yearsOfExperience,
      salary: submittedRate ? formatCurrency(submittedRate) : null,
    });
  }, [jobPosition, yearsOfExperience, submittedRate]);

  useEffect(() => {
    handleFormChange();
  }, []);

  const hasErrors = (fieldsError) => {
    return Object.keys(fieldsError).some((field) => fieldsError[field]);
  };

  const onFinish = (e) => {
    e.preventDefault();
    const {
      yearsOfExperience: years_of_experience = null,
      salary: expected_salary_amount_yearly = "0",
    } = getFieldsValue();

    saveProfileData(
      {
        years_of_experience: Number(years_of_experience),
        salary: Number(expected_salary_amount_yearly.replace(dotReg, "")),
      },
      (rateUpdated) => {
        if (rateUpdated) {
          redirectIfNotCurrent(DevUpdateUrls.SUCCESS);
        } else {
          redirectIfNotCurrent(DevUpdateUrls.ATTEMPT);
        }
      }
    );
  };

  const yearsOfExperienceSuffix = (
    <Form.Item name="yearsOfExperienceSuffix" style={suffixPrefixStyle}>
      <span>years of experience</span>
    </Form.Item>
  );

  const salarySuffix = (
    <Form.Item name="salarySuffix" style={suffixPrefixStyle}>
      <b> {expectedRateCurrency} per year</b>
    </Form.Item>
  );

  const isFormValid = () => {
    const { yearsOfExperience, salary } = getFieldsValue();
    const fieldErrors = getFieldsError();

    return !!yearsOfExperience && !!salary && !hasErrors(fieldErrors);
  }

  const handleFormChange = () => {
    if (isFormValid()) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };

  const onSalaryChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]+/g, "");
    if (value) {
      const formatedValue = formatCurrency(value);
      if (formatedValue !== "NaN") return formatedValue;
    }
    return "";
  }, []);

  const onYoeChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9]+/g, "");
    if (value) return parseInt(value, 10);
    return "";
  }, []);

  const renderYearsOfExperienceInput = () => (
    <Form.Item label="Years of experience">
      <span className="sublabel">
        How many years of full-time work experience (directly related to your role) do you have?
      </span>
      {getFieldDecorator("yearsOfExperience", {
        getValueFromEvent: onYoeChange,
        rules: [
          {
            required: true,
            message: "Please input years of experience!",
          },
          {
            min: 1,
            max: 50,
            type: "number",
            message: "Years of experience must be between 1 - 50",
          },
        ],
      })(<Input suffix={yearsOfExperienceSuffix} min={1} maxLength={2} />)}
    </Form.Item>
  );

  const showRateUpdateElements = remainingAttempts <= 2;
  const showRemainingAttemptWarning = !hasErrors(getFieldsError()) && showRateUpdateElements;

  const renderExpectedCompensationInput = () => (
    <Form.Item label="Expected annual rate">
      <span className="sublabel">
        What is your expected <span className="highlight"><b>annual</b></span> rate? <br />
      </span>
      {dev_rate_reduction_show_current_rate && <CurrentSalaryInfo />}
      <div className="sublabel">
        New expected rate:
      </div>
      {getFieldDecorator("salary", {
        getValueFromEvent: onSalaryChange,
        rules: [
          {
            required: true,
            transform: (value) =>
              isNaN(value.replace(dotReg, "")) ? null : parseFloat(value, 10),
            type: "number",
            min: 1,
            message: "Annual rate must be a positive number",
          },
        ],
      })(<Input suffix={salarySuffix} maxLength={14} min={0} />)}
      {showRemainingAttemptWarning && (
        <span className="ant-form-explain">
          <RemainingAttemptsText type="normal" showFinal={false} />
        </span>
      )}
    </Form.Item>
  );

  const renderSubmitButton = () => (
    <Form.Item>
      <Button
        type="primary"
        size="large"
        disabled={isDisabled}
        htmlType="submit"
      >
        Update Information
      </Button>
    </Form.Item>
  );

  return (
    <>
      <Header title={`Hi, ${firstName || ""}!`}>
        <p>
          As you are aware, the ongoing macro slowdown has affected the global economy, and many companies have had to make difficult decisions regarding their budgets, including reducing the rate they are paying to new contractors.
        </p>
        <p>
          To improve your chances of being matched to a client, we encourage you to review your minimum rate to align with the current market conditions. 
        </p>
        <p>
          <b>
            Reducing your rate by 20% can open up more jobs and improve your chances of being matched to clients.
          </b>
        </p>
      </Header>
      <Form onSubmit={onFinish} onChange={handleFormChange}>
        <div className="form-container">
          <h3>Review and update your information</h3>

          {renderYearsOfExperienceInput()}

          {renderExpectedCompensationInput()}

          {renderSubmitButton()}
        </div>
      </Form>
    </>
  );
};

export default Form.create({ name: "rate_update_form" })(withLDFlagsFromBackend(RateUpdateForm, [GB_FLAGS.dev_rate_reduction_show_current_rate]));
